import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { calcularComissao, SALE_STATUS_LABELS, type SaleStatus } from '@agencia/shared';
import { useCurrentUser } from '@/auth/AuthContext';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatBRL, formatDate, formatDateTime, nomeMes, parseMoneyInput, toInputMoney } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { PagamentoVenda } from '@/components/PagamentoVenda';

interface SaleDetail {
  id: string;
  numero: number | null;
  clienteNome: string;
  dataVenda: string;
  competencia: string;
  valorBruto: string;
  custoTotal: string | null;
  lucro: string | null;
  comissaoValor: string | null;
  status: SaleStatus;
  vendedorId: string | null;
  vendedorNome: string | null;
  editavel: boolean;
  motivoBloqueio: string | null;
  comissaoPercentual: string;
  prazoCusto: string;
  custoInformadoEm: string | null;
  itens: Array<{ id: string; productTypeId: string; tipo: string; descricao: string; custo: string }>;
}

interface Linha {
  key: number;
  productTypeId: string;
  descricao: string;
  custo: string; // como digitado: "1.234,56"
}

let seq = 0;
const novaLinha = (productTypeId = ''): Linha => ({ key: ++seq, productTypeId, descricao: '', custo: '' });

/** Lançamento do custo de uma venda: o que foi vendido (itens) e quanto custou. */
export function SaleCostPage() {
  const { id = '' } = useParams();
  const me = useCurrentUser();
  const navigate = useNavigate();
  const { data: sale, error: loadError, setData } = useApi<SaleDetail>(`/sales/${id}`);
  const { data: tipos } = useApi<Array<{ id: string; nome: string }>>('/product-types');
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Preenche o formulário com os itens já lançados (ou uma linha vazia).
  useEffect(() => {
    if (!sale) return;
    setLinhas(
      sale.itens.length
        ? sale.itens.map((i) => ({ key: ++seq, productTypeId: i.productTypeId, descricao: i.descricao, custo: toInputMoney(i.custo) }))
        : [novaLinha()],
    );
  }, [sale]);

  const preview = useMemo(() => {
    if (!sale) return null;
    const valores = linhas.map((l) => parseMoneyInput(l.custo));
    if (valores.some((v) => v === null)) return null;
    const totalCents = (valores as string[]).reduce((acc, v) => acc + Math.round(Number(v) * 100), 0);
    const custo = `${Math.floor(totalCents / 100)}.${String(totalCents % 100).padStart(2, '0')}`;
    return { custo, ...calcularComissao(sale.valorBruto, custo, sale.comissaoPercentual) };
  }, [linhas, sale]);

  const update = (key: number, patch: Partial<Linha>) => setLinhas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const itens = [];
    for (const [i, l] of linhas.entries()) {
      const custo = parseMoneyInput(l.custo);
      if (!l.productTypeId) return setError(`Escolha o tipo do item ${i + 1}.`);
      if (!l.descricao.trim()) return setError(`Descreva o item ${i + 1} (ex.: "Passagem GRU–LIS ida e volta").`);
      if (custo === null) return setError(`Custo inválido no item ${i + 1}. Use o formato 1.234,56.`);
      itens.push({ productTypeId: l.productTypeId, descricao: l.descricao.trim(), custo });
    }
    setSaving(true);
    try {
      const r = await api.put<SaleDetail>(`/sales/${id}/costs`, { itens });
      setData(r);
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <div className="page">
        <Alert>{loadError}</Alert>
        <Link to="/vendas">Voltar para as vendas</Link>
      </div>
    );
  }
  if (!sale) return <p className="muted">Carregando…</p>;

  const mine = sale.vendedorId === me.id;

  return (
    <div className="page narrow-page">
      <Link to={`/vendas?mes=${sale.competencia}`} className="back">
        ← Vendas de {nomeMes(sale.competencia)}
      </Link>
      <header>
        <p className="eyebrow">{sale.editavel ? 'Informar custo da venda' : 'Venda'}</p>
        <h1 className="page-title">
          #{sale.numero ?? '—'} {sale.clienteNome}
        </h1>
        <p className="muted">
          {formatDate(sale.dataVenda)}
          {!mine && sale.vendedorNome && <> · vendedor: {sale.vendedorNome}</>} ·{' '}
          <span className={`badge badge--${sale.status === 'CUSTO_INFORMADO' ? 'ok' : sale.status === 'AGUARDANDO_CUSTO' ? 'warn' : 'off'}`}>
            {SALE_STATUS_LABELS[sale.status]}
          </span>
        </p>
      </header>

      <PagamentoVenda saleId={sale.id} titulo={`Venda #${sale.numero ?? ''} · ${sale.clienteNome}`.slice(0, 120)} />

      <section className="card value-card">
        <span className="muted small">Valor da venda (vem do Conta Azul)</span>
        <strong className="value-card__value">{formatBRL(sale.valorBruto)}</strong>
      </section>

      {saved && (
        <Alert kind="success">
          Custos salvos. Sua comissão nesta venda: <strong>{formatBRL(sale.comissaoValor ?? '0')}</strong>.{' '}
          <button type="button" className="linklike" onClick={() => void navigate(`/vendas?mes=${sale.competencia}&filtro=pendentes`)}>
            Ir para a próxima venda sem custo
          </button>
        </Alert>
      )}
      {sale.motivoBloqueio && <Alert kind="info">{sale.motivoBloqueio}</Alert>}
      {error && <Alert>{error}</Alert>}

      {sale.editavel ? (
        <form className="card" onSubmit={(e) => void submit(e)}>
          <h2>O que foi vendido</h2>
          <div className="items">
            <div className="items__head" aria-hidden="true">
              <span>Tipo</span>
              <span>Descrição</span>
              <span>Custo</span>
              <span />
            </div>
            {linhas.map((l, i) => (
              <div className="items__row" key={l.key}>
                <select aria-label={`Tipo do item ${i + 1}`} value={l.productTypeId} onChange={(e) => update(l.key, { productTypeId: e.target.value })} required>
                  <option value="">Tipo…</option>
                  {(tipos ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome}
                    </option>
                  ))}
                </select>
                <input
                  aria-label={`Descrição do item ${i + 1}`}
                  placeholder="Ex.: Passagem SSA–LIS ida e volta"
                  value={l.descricao}
                  maxLength={255}
                  onChange={(e) => update(l.key, { descricao: e.target.value })}
                  required
                />
                <input
                  aria-label={`Custo do item ${i + 1}`}
                  inputMode="decimal"
                  placeholder="0,00"
                  className="money"
                  value={l.custo}
                  onChange={(e) => update(l.key, { custo: e.target.value })}
                  onBlur={() => {
                    const v = parseMoneyInput(l.custo);
                    if (v) update(l.key, { custo: toInputMoney(v) });
                  }}
                  required
                />
                <button
                  type="button"
                  className="icon-btn icon-btn--light"
                  aria-label={`Remover item ${i + 1}`}
                  disabled={linhas.length === 1}
                  onClick={() => setLinhas((ls) => ls.filter((x) => x.key !== l.key))}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div>
            <Button type="button" variant="ghost" onClick={() => setLinhas((ls) => [...ls, novaLinha()])} disabled={linhas.length >= 30}>
              + Adicionar item
            </Button>
          </div>

          <dl className="summary">
            <div>
              <dt>Custo total</dt>
              <dd>{preview ? formatBRL(preview.custo) : '—'}</dd>
            </div>
            <div>
              <dt>Lucro</dt>
              <dd className={preview?.prejuizo ? 'neg' : undefined}>{preview ? formatBRL(preview.lucro) : '—'}</dd>
            </div>
            <div className="summary__main">
              <dt>{mine ? 'Sua comissão' : 'Comissão'}</dt>
              <dd>{preview ? formatBRL(preview.comissao) : '—'}</dd>
            </div>
          </dl>
          {preview?.prejuizo && <Alert kind="warning">O custo está maior que o valor da venda. Confira os valores antes de salvar.</Alert>}

          <p className="muted small">
            Toda alteração fica registrada. Depois do fechamento de {nomeMes(sale.competencia)}, os custos ficam travados.
            {sale.custoInformadoEm && <> Último lançamento: {formatDateTime(sale.custoInformadoEm)}.</>}
          </p>
          <div className="row">
            <Button type="submit" loading={saving}>
              Salvar custos
            </Button>
            <Button type="button" variant="ghost" onClick={() => void navigate(-1)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : (
        sale.itens.length > 0 && (
          <section className="card">
            <h2>O que foi vendido</h2>
            <table className="table table--plain">
              <tbody>
                {sale.itens.map((i) => (
                  <tr key={i.id}>
                    <td>{i.tipo}</td>
                    <td>{i.descricao}</td>
                    <td className="num">{formatBRL(i.custo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <dl className="summary">
              <div>
                <dt>Custo total</dt>
                <dd>{formatBRL(sale.custoTotal ?? '0')}</dd>
              </div>
              <div>
                <dt>Lucro</dt>
                <dd>{formatBRL(sale.lucro ?? '0')}</dd>
              </div>
              <div className="summary__main">
                <dt>Comissão</dt>
                <dd>{formatBRL(sale.comissaoValor ?? '0')}</dd>
              </div>
            </dl>
          </section>
        )
      )}
    </div>
  );
}
